import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getStoredProgress,
  updateOrAppendProgress,
} from "./reportProgressUtils";

interface UseProgressOptions {
  progressMessage: string;
  reportStatus: string;
  reportId: number;
  projectId: string;
  taskId: string;
  allowNavigation?: boolean;
}

const PROGRESS_STEPS: { match: string; range: [number, number] }[] = [
  { match: "processing files", range: [1, 5] },
  { match: "planning", range: [5, 25] },
  { match: "analyzing data", range: [25, 50] },
  { match: "generating report", range: [50, 95] },
  { match: "finalizing", range: [95, 98.9] },
];

const SECONDS_PER_PERCENT = 3.9;

export function useProgressWithNavigation({
  progressMessage,
  reportStatus,
  reportId,
  projectId,
  taskId,
  allowNavigation = false,
}: UseProgressOptions) {
  const [progress, setProgress] = useState<number>(() =>
    getStoredProgress(taskId)
  );
  const navigate = useNavigate();

  const currentStepKeyRef = useRef<string | null>(null);
  const baseProgressRef = useRef(progress);
  const startTimeRef = useRef(Date.now());
  const stepDurationRef = useRef<number>(0);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (reportStatus === "FAILURE") return;

      let range: [number, number] = [0, 5];
      let stepKey = "";

      if (reportStatus === "SUCCESS") {
        range = [100, 100];
        stepKey = "completed";
      } else if (reportStatus === "PENDING") {
        range = [0, 0];
        stepKey = "pending";
      } else {
        const matchedStep = PROGRESS_STEPS.find(({ match }) =>
          progressMessage?.toLowerCase().includes(match)
        );
        if (matchedStep) {
          range = matchedStep.range;
          stepKey = matchedStep.match;
        }
      }

      const [min, max] = range;

      if (currentStepKeyRef.current !== stepKey) {
        currentStepKeyRef.current = stepKey;
        const newBase = progress < min ? min : progress;
        baseProgressRef.current = newBase;
        startTimeRef.current = Date.now();
        stepDurationRef.current = (max - newBase) * SECONDS_PER_PERCENT;
        setProgress(newBase);
        updateOrAppendProgress(taskId, newBase);
        return;
      }

      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const rangeToCover = max - baseProgressRef.current;
      const estimated =
        baseProgressRef.current +
        (elapsed / stepDurationRef.current) * rangeToCover;

      if (progress < max) {
        const updated = Math.min(estimated, max);
        setProgress(updated);
        updateOrAppendProgress(taskId, updated);
      }

      if (
        reportStatus === "SUCCESS" &&
        estimated >= 100 &&
        !hasNavigatedRef.current
      ) {
        hasNavigatedRef.current = true;
        updateOrAppendProgress(taskId, 100);
        if (allowNavigation) {
          setTimeout(() => {
            navigate(`/report/${reportId}/project/${projectId}`);
          }, 300);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [progressMessage, reportStatus, reportId, projectId, navigate, progress]);

  return progress;
}
